import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { TrendingUp, Users, DollarSign, BarChart2, ArrowUpRight } from 'lucide-react';

const COLORS = ['#00c566','#f5c842','#ff6b35','#0e8fff','#ff4444'];

export default function Analytics() {
  const [period, setPeriod] = useState('7d');

  const volumeData = Array.from({length:30},(_,i)=>({ day:`${i+1}`, volume:Math.floor(Math.random()*800+200), trades:Math.floor(Math.random()*40+10) }));
  const categoryData = [
    {name:'Crypto',   value:45},
    {name:'Politics', value:25},
    {name:'Sports',   value:15},
    {name:'Economy',  value:10},
    {name:'Other',    value:5},
  ];
  const funnelData = [
    {name:'Link Clicks',    value:1240},
    {name:'App Opens',      value:680},
    {name:'Wallet Created', value:312},
    {name:'First Trade',    value:156},
  ];

  return (
    <div>
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontSize:28, fontWeight:800, fontFamily:"'Syne',sans-serif", marginBottom:4 }}>Analytics</h1>
        <p style={{ fontSize:14, color:'#4a5a63' }}>Track your market performance and audience engagement</p>
      </div>

      {/* Period Selector */}
      <div style={{ display:'flex', gap:8, marginBottom:24 }}>
        {['7d','30d','90d','all'].map(p=>(
          <button key={p} onClick={()=>setPeriod(p)} className="btn" style={{ padding:'6px 14px', fontSize:12, background:period===p?'rgba(0,197,102,.1)':'rgba(255,255,255,.04)', color:period===p?'#00c566':'#4a5a63', border:period===p?'1px solid rgba(0,197,102,.2)':'1px solid rgba(255,255,255,.08)' }}>
            {p}
          </button>
        ))}
      </div>

      {/* KPI Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:12, marginBottom:20 }}>
        {[
          [TrendingUp,'Total Volume',    '$2,450','+12%','#00c566'],
          [Users,     'Unique Traders',  '87',    '+8%', '#f5c842'],
          [BarChart2, 'Total Trades',    '312',   '+15%','#ff6b35'],
          [DollarSign,'Royalties',       '$12.25','+12%','#00c566'],
        ].map(([Icon,label,val,trend,color])=>(
          <div key={label} className="card">
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
              <Icon size={16} color={color}/>
              <span style={{ fontSize:12, color:'#00c566', fontWeight:600 }}>{trend} <ArrowUpRight size={10}/></span>
            </div>
            <div style={{ fontSize:22, fontWeight:800, fontFamily:"'Syne',sans-serif" }}>{val}</div>
            <div style={{ fontSize:12, color:'#4a5a63', marginTop:2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Volume Chart */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ fontSize:15, fontWeight:700, marginBottom:4 }}>Trading Volume Over Time</div>
        <div style={{ fontSize:12, color:'#4a5a63', marginBottom:20 }}>Daily USDC volume across all your markets</div>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={volumeData.slice(-parseInt(period)||30)}>
            <defs>
              <linearGradient id="aGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#00c566" stopOpacity={0.15}/>
                <stop offset="95%" stopColor="#00c566" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis dataKey="day" tick={{fill:'#4a5a63',fontSize:10}} axisLine={false} tickLine={false} interval={4}/>
            <YAxis tick={{fill:'#4a5a63',fontSize:10}} axisLine={false} tickLine={false} width={35}/>
            <Tooltip contentStyle={{background:'#0d1114',border:'1px solid rgba(255,255,255,.08)',borderRadius:8,fontSize:12}}/>
            <Area type="monotone" dataKey="volume" stroke="#00c566" strokeWidth={2} fill="url(#aGrad)" dot={false}/>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:16, marginBottom:16 }}>
        {/* Category Breakdown */}
        <div className="card">
          <div style={{ fontSize:15, fontWeight:700, marginBottom:16 }}>Volume by Category</div>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={categoryData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" paddingAngle={3}>
                {categoryData.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
              </Pie>
              <Tooltip contentStyle={{background:'#0d1114',border:'1px solid rgba(255,255,255,.08)',borderRadius:8,fontSize:12}}/>
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:8 }}>
            {categoryData.map((c,i)=>(
              <div key={c.name} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#4a5a63' }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:COLORS[i%COLORS.length] }}/>
                {c.name} {c.value}%
              </div>
            ))}
          </div>
        </div>

        {/* Conversion Funnel */}
        <div className="card">
          <div style={{ fontSize:15, fontWeight:700, marginBottom:16 }}>Conversion Funnel</div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {funnelData.map((f,i)=>{
              const pct = Math.round(f.value/funnelData[0].value*100);
              return (
                <div key={f.name}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                    <span style={{ color:'#4a5a63' }}>{f.name}</span>
                    <span style={{ fontWeight:600 }}>{f.value.toLocaleString()} <span style={{ color:'#4a5a63' }}>({pct}%)</span></span>
                  </div>
                  <div style={{ height:6, background:'rgba(255,255,255,.06)', borderRadius:3, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:pct+'%', background:COLORS[i], borderRadius:3, transition:'width .5s' }}/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Trades Chart */}
      <div className="card">
        <div style={{ fontSize:15, fontWeight:700, marginBottom:4 }}>Daily Trade Count</div>
        <div style={{ fontSize:12, color:'#4a5a63', marginBottom:20 }}>Number of trades per day</div>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={volumeData.slice(-14)}>
            <XAxis dataKey="day" tick={{fill:'#4a5a63',fontSize:10}} axisLine={false} tickLine={false}/>
            <YAxis tick={{fill:'#4a5a63',fontSize:10}} axisLine={false} tickLine={false} width={30}/>
            <Tooltip contentStyle={{background:'#0d1114',border:'1px solid rgba(255,255,255,.08)',borderRadius:8,fontSize:12}}/>
            <Bar dataKey="trades" fill="#00c566" radius={[4,4,0,0]} opacity={0.8}/>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
